import { Button, Grid } from "@mui/material";
import { FormAutoCompleteTableSelect } from "../../../components/hook-form/form-autocomplete-table-select.dynamic.component";
import FormInput from "../../query-builder/hooks-form-querybuilder/form-input.component";
import { useAuthorize } from "../../../hooks/use-authorize";
import { UserPermissionName } from "../../../models/user-permission-name";
import { useFormContext } from "react-hook-form";
import { useLoadData } from "../../../hooks/useLoadData";
import { ApiResponse } from "../../../models/api-response";
import { projectsApiKeyStore } from "../../../api/projects-api-key-store";
import { useEffect, useState } from "react";
import { GuardedComponent } from "../../../components/guarded-component";

interface ServiceNewPriceFields {
    projectID: any;
    refetchPricing?: boolean;
    applicablePricingListIDs?: any
    onPriceSelect: (pricingData: any) => void;
    openNewPriceDialog?: () => void;
    openEditPriceDialog?: (pricingId: any) => void;
    applyPriceConfirm?: (pricingId: any) => void;
}

export const ProjectServiceNewPriceFields = ({ projectID, refetchPricing, applicablePricingListIDs, onPriceSelect, openNewPriceDialog, openEditPriceDialog, applyPriceConfirm }: ServiceNewPriceFields) => {

    const { setValue, getValues } = useFormContext();
    const [selectedPrice, setSelectedPrice] = useState<any>(null);
    const [pricingList, setPricingList] = useState<any>([]);
    const [isDisabledSelectedPrice, setDisabledSelectedPrice] = useState<boolean>(true);

    const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any[]>>(projectsApiKeyStore.getProjectPricingListByProject(projectID));

    const pricingOptionMap: Record<any, string> = {
        AllNewStarts: 'All New Starts',
        AllLots: 'All Lots',
        AllNewTH: 'All New TH',
        AllNewSFR: 'All New SFR',
        AllNewCM: 'All New Commercial (CM)'
    };

    useEffect(() => { refetch(); }, [refetchPricing, getValues('pricingListID')])

    const showUpdatedPricing = (updatedPrice: any) => {
        setSelectedPrice(updatedPrice);
        onPriceSelect(updatedPrice);
        if (updatedPrice)
            setDisabledSelectedPrice(applicablePricingListIDs?.includes(updatedPrice?.pricingListID));
        setValue('projectPrice', { pricingListID: updatedPrice?.pricingListID ?? '', pricingListName: updatedPrice?.pricingListName ?? "" });
        setValue('priceStartDate', updatedPrice?.pricingStartDate ?? '');
        setValue('priceEndDate', updatedPrice?.pricingEndDate ?? '');
        setValue('priceApplyTo', updatedPrice?.pricingListAppliesToLotTypeLabel ?? '');
    }

    useEffect(() => {
        if (data) {
            const updatedPriceData = data?.data?.map(item => ({
                ...item,
                pricingListAppliesToLotTypeLabel:
                    pricingOptionMap[item.pricingListAppliesToLotType] || null,
                  statusLabel:
                       !item.isPricingListEffectiveToday && item.isPricingListCurrentlyApplied
                                    ? "⚠️ Overridden (Expired)"
                                    :   item.isPricingListCurrentlyApplied
                                    ? "⚠️ Overridden"
                                    : item.isPricingListEffectiveToday
                                        ? "✅ Effective"
                                        :
                    item.isPricingListCurrentlyApplied
                        ? "⚠️ Overridden"
                        : ""
            }));
            if (updatedPriceData?.length > 0) {
                const setPrice = updatedPriceData?.find((item: any) => item?.pricingListID === selectedPrice?.pricingListID)
                showUpdatedPricing(setPrice);
            }
            if (getValues('pricingListID')) {
                const setPrice = updatedPriceData?.find((item: any) => item?.pricingListID === getValues('pricingListID'))
                showUpdatedPricing(setPrice);
            }
            setPricingList(updatedPriceData);
        }
    }, [data && isFetching])

    const handlePriceChange = (row: any) => {
        setSelectedPrice(row);
        onPriceSelect(row);
        if (row) {
            setValue('priceStartDate', row?.pricingStartDate ?? '');
            setValue('priceEndDate', row?.pricingEndDate ?? '');
            setValue('priceApplyTo', row?.pricingListAppliesToLotTypeLabel ?? '');
            setDisabledSelectedPrice(applicablePricingListIDs?.includes(row?.pricingListID));
        }
        else {
            setValue('priceStartDate', "");
            setValue('priceEndDate', "");
            setValue('priceApplyTo', "");
            setDisabledSelectedPrice(true);
        }
    };

    const handleOpenEditPrice = (pricingListID: any) => {
        openEditPriceDialog?.(pricingListID);
    }

    const handleOpenpplyPrice = (pricingListID: any) => {
        applyPriceConfirm?.(pricingListID);
    }

    const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

    return (
        <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid container spacing={2}>
                <Grid item lg={2} sm={2} xl={2} xs={2}>
                    <FormAutoCompleteTableSelect
                        name="projectPrice"
                        label="Price List Name"
                        dataItems={pricingList}
                        onChange={handlePriceChange}
                        idKey="pricingListID"
                        optionWidth={'900px'}
                        isParentLoading={isLoading || isFetching}
                        getOptionLabel={(o) => o.pricingListName}
                        columns={[
                            { header: "Pricing List Name", field: "pricingListName", width: "2fr" },
                            { header: "Start Date", field: "pricingStartDate", width: "1fr" },
                            { header: "End Date", field: "pricingEndDate", width: "1fr" },
                            { header: "Pricing List Applies To", field: "pricingListAppliesToLotTypeLabel", width: "2fr" },
                            { header: "Status", field: "statusLabel", width: "2fr" }
                        ]}
                        //getRowStyle={(row: any) => row.isPricingListEffectiveToday ? { backgroundColor: '#dff3e0' } : {}}
                    />
                </Grid>
                <Grid item lg={1.1} sm={1.1} xl={1.1} xs={1.1}>
                    <FormInput name="priceStartDate" label="Pricing Start Date" disabled />
                </Grid>
                <Grid item lg={1.1} sm={1.1} xl={1.1} xs={1.1}>
                    <FormInput name="priceEndDate" label="Pricing End Date" disabled />
                </Grid>
                <Grid item lg={2} sm={2} xl={2} xs={2}>
                    <FormInput name="priceApplyTo" label="Pricing List Applies To" disabled />
                </Grid>
                <GuardedComponent requiredPermissions={[UserPermissionName.EDIT_PRICING_LIST]}>
                    <Grid item lg={1.2} sm={1.2} xl={1.2} xs={1.2}>
                        <Button
                            type={'button'}
                            variant="contained"
                            fullWidth
                            onClick={() => handleOpenEditPrice(selectedPrice?.pricingListID)}
                            disabled={selectedPrice == null || isReadOnlyPermission}
                        >
                            Edit Price List
                        </Button>
                    </Grid>
                </GuardedComponent>
                <GuardedComponent requiredPermissions={[UserPermissionName.ADD_PRICING_LIST]}>
                    <Grid item lg={1.3} sm={1.3} xl={1.3} xs={1.3}>
                        <Button
                            type={'button'}
                            fullWidth
                            variant="contained"
                            onClick={openNewPriceDialog}
                            disabled={isReadOnlyPermission}
                        >
                            Add New Price List
                        </Button>
                    </Grid>
                </GuardedComponent>
                <GuardedComponent requiredPermissions={[UserPermissionName.OVERRIDE_PRICING_LIST]}>
                    <Grid item lg={1.7} sm={1.7} xl={1.7} xs={1.7}>
                        <Button
                            type={'button'}
                            fullWidth
                            variant="contained"
                            onClick={() => handleOpenpplyPrice(selectedPrice?.pricingListID)}
                            disabled={isDisabledSelectedPrice || isReadOnlyPermission}
                        >
                            Apply Selected Price List
                        </Button>
                    </Grid>
                </GuardedComponent>
            </Grid>
        </Grid>
    );
}